import { User } from 'nomst/Stores/User/user';
import { configurationsStore, workflowSettingsViewStore } from 'utils/MobxDataManager/singletons';
import { ManagerWorkflowStore } from 'nomst/Stores/Decision/managerWorkflow.store';
import { CycleStatus } from 'typings/enums';

export class CycleRules {
    private readonly _user: User;
    private readonly _workflowStore: ManagerWorkflowStore;

    constructor(user: User, managerWorkflowStore: ManagerWorkflowStore) {
        this._user = user;
        this._workflowStore = managerWorkflowStore;
    }

    get isCurrentCycleLocked(): boolean {
        if (this._user.isSupportOnly ||
            configurationsStore.currentConvertedConfiguration?.status === CycleStatus.Pending ||
            configurationsStore.currentConvertedConfiguration?.status === CycleStatus.Published) {
            return true;
        }

        if (this._user.hasHrRole) {
            return false;
        }

        if (this._user.isRoleOnlyManager &&
            !!configurationsStore.currentConvertedConfiguration?.teamPageViews.lockViewForManager) {
            return true;
        }

        const userIsAssignedHrbp = this._user.isHrbp && this._workflowStore.isUserInWorkflowAsHr(this._user);
        return !userIsAssignedHrbp && (this.isCurrentCycleInPreviewMode ||
            configurationsStore.currentConvertedConfiguration?.status === CycleStatus.Closing ||
            configurationsStore.currentConvertedConfiguration?.status === CycleStatus.Closed);
    }

    get isManagerWorkflowLocked(): boolean {
        if (this.isCurrentCycleLocked) {
            return true;
        }

        // allow HR Assistants to send for review/send back without being in workflow
        if (this._user.isHrAssistant) {
            return false;
        }

        const { selectedManagerWorkflows } = this._workflowStore;
        if (!selectedManagerWorkflows.length) {
            return true;
        }

        const managerWorkflow = selectedManagerWorkflows[0];
        if (managerWorkflow.isApproved) {
            return true;
        }

        const currentUserAsReviewerIndex =
            this._workflowStore.getCurrentUserAsReviewerIndex(managerWorkflow, this._user);
        if (currentUserAsReviewerIndex === -1) {
            return true;
        }

        return currentUserAsReviewerIndex < managerWorkflow.currentReviewerIndex;
    }

    get isCurrentCyclePendingOrPreview(): boolean {
        return this.isCyclePendingOrPreview(configurationsStore.currentCycleId);
    }

    isCyclePendingOrPreview(id: number): boolean {
        const config = configurationsStore.getSpecificConfiguration(id);
        return config.isCurrentlyPending || config.isInPreviewMode;
    }

    get hasCycleOpenedNotPublished(): boolean {
        const config = configurationsStore.currentConvertedConfiguration;
        return !!config.cycleId && !config.isCurrentlyPending && !config.isPublished;
    }

    get isCurrentCycleClosed(): boolean {
        return this.cycleClosedRules(configurationsStore.currentCycleId);
    }

    get isCurrentCycleInPreviewMode(): boolean {
        return this.isCycleInPreviewModeForId(configurationsStore.currentCycleId);
    }

    get isCurrentCycleClosedOrPublished(): boolean {
        const config = configurationsStore.currentConvertedConfiguration;
        return config.isClosed || config.isPublished;
    }

    get isCurrentCyclePublished(): boolean {
        const config = configurationsStore.currentConvertedConfiguration;
        return config.isPublished;
    }

    isCycleInPreviewModeForId(id: number): boolean {
        const config = configurationsStore.getSpecificConfiguration(id);
        return config.status === CycleStatus.Open && config.previewMode === true;
    }

    isCycleClosedForId(id: number): boolean {
        return this.cycleClosedRules(id);
    }

    get isSelectedCycleClosed(): boolean {
        return this.isCycleClosedForId(configurationsStore.cycleSettingsId);
    }

    get isCycleClosedAndNotCurrent(): boolean {
        if (!configurationsStore.cycleSettingsViewCycle) {
            return false;
        }
        return this.isCycleClosedForId(configurationsStore.cycleSettingsViewCycle.cycleId) &&
            (configurationsStore.anyOpenCycleConfigurations ||
            !!configurationsStore.cycleConfigurations.find(config => config.status === CycleStatus.Pending));
    }

    get isCyclePendingAndUnpublishedCycleExists(): boolean {
        return configurationsStore.cycleSettingsViewCycle?.status === CycleStatus.Pending &&
            !!configurationsStore.cycleConfigurations.find(cycle => cycle.status === CycleStatus.Closed);
    }

    get anyOpenOrClosingCycles(): boolean {
        return !!configurationsStore.cycleConfigurations.find(config =>
            config.status === CycleStatus.Open || config.status === CycleStatus.Closing);
    }

    isCyclePreviewable(id: number): boolean {
        const config = configurationsStore.getSpecificConfiguration(id);
        return config.isCurrentlyPending
            && !this.anyOpenOrClosingCycles
            && !!workflowSettingsViewStore.assignUserHrApprover.selectedUserId
            && !!config.cycleBudgets.length;
    }

    isAmountInBounds(value: number, targetPercent: number): boolean {
        const currentCycle = configurationsStore.currentConvertedConfiguration;
        if (!this._user.isRoleOnlyManager || !currentCycle?.isManagerIncreaseRestricted || !targetPercent) {
            return true;
        }

        const min = currentCycle.managerIncreasePercentageUnder
            ? targetPercent - currentCycle.managerIncreasePercentageUnder
            : 0;
        const max = currentCycle.managerIncreasePercentageOver
            ? targetPercent + currentCycle.managerIncreasePercentageOver
            : Number.MAX_SAFE_INTEGER;
        return value >= min && value <= max;
    }

    private cycleClosedRules(id: number): boolean {
        const config = configurationsStore.getSpecificConfiguration(id);
        return config?.status === CycleStatus.Closed ||
            config?.status === CycleStatus.Closing ||
            config?.status === CycleStatus.Published;
    }
}